import React, { useState } from "react";
import { ButtonProps, ButtonState } from "../../types/Button";
import { Icon } from "../Icon";
import typographyTokens from "../../styles/tokens/typography";
import * as tokens from "../../styles/tokens/tokens";

export const Button: React.FC<ButtonProps> = ({
  label,
  type = "primary",
  size = "large",
  disabled = false,
  leftIcon,
  rightIcon,
  icon,
  fullWidth = false,
  style,
  accessibilityLabel,
  testID,
  onClick,
  className,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
}) => {
  const isIconOnly = !!icon;

  // Create icon components from icon names
  const leftIconComponent = leftIcon ? (
    <Icon name={leftIcon} size={size === "large" ? 20 : 16} />
  ) : null;
  const rightIconComponent = rightIcon ? (
    <Icon name={rightIcon} size={size === "large" ? 20 : 16} />
  ) : null;
  const iconButton = icon ? (
    <Icon name={icon} size={size === "large" ? 20 : 16} />
  ) : null;

  const [buttonState, setButtonState] = useState<ButtonState>(
    disabled ? "disabled" : "default"
  );

  const hasBothIcons = leftIcon && rightIcon;

  // Base button styles
  const getBaseStyles = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: isIconOnly ? "inline-flex" : "flex",
      width: isIconOnly ? "auto" : fullWidth ? "100%" : "9.375rem",
      height: size === "large" ? "2.75rem" : "2rem",
      padding: type === "ghost" ? tokens.SPACE_0 : tokens.COMPONENT_CARD_SM,
      justifyContent: "center",
      alignItems: "center",
      gap: hasBothIcons
        ? tokens.LAYOUT_SPACING_INLINE_MD
        : tokens.LAYOUT_SPACING_INLINE_XS,
      flexShrink: 0,
      borderRadius: tokens.BORDER_RADIUS_BUTTON_FULL,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      transition: "all 0.2s ease-in-out",
      border: "none",
    };

    // Apply styles based on type and state
    switch (type) {
      case "primary":
        switch (buttonState) {
          case "default":
            baseStyle.background = tokens.ACTION_PRIMARY_DEFAULT;
            break;
          case "hover":
            baseStyle.background = tokens.ACTION_PRIMARY_HOVER;
            break;
          case "active":
            baseStyle.background = tokens.ACTION_PRIMARY_ACTIVE;
            break;
          case "disabled":
            baseStyle.background = tokens.ACTION_PRIMARY_DISABLED;
            baseStyle.opacity = 0.6;
            break;
        }
        break;
      case "outline":
        baseStyle.background = "transparent";
        switch (buttonState) {
          case "default":
            baseStyle.border = `${tokens.BORDER_WIDTH_BUTTON_DEFAULT} solid ${tokens.ACTION_TERTIARY_DEFAULT}`;
            break;
          case "hover":
            baseStyle.border = `${tokens.BORDER_WIDTH_BUTTON_HOVER} solid ${tokens.ACTION_TERTIARY_HOVER}`;
            break;
          case "active":
            baseStyle.border = `${tokens.BORDER_WIDTH_BUTTON_PRESSED} solid ${tokens.ACTION_TERTIARY_ACTIVE}`;
            break;
          case "disabled":
            baseStyle.border = `${tokens.BORDER_WIDTH_BUTTON_DEFAULT} solid ${tokens.ACTION_TERTIARY_DISABLE}`;
            baseStyle.opacity = 0.6;
            break;
        }
        break;
      case "ghost":
        baseStyle.background = "transparent";
        break;
      case "destructive":
        baseStyle.background = "transparent";
        switch (buttonState) {
          case "default":
            baseStyle.color = tokens.ACTION_DESTRUCTIVE_HOVER;
            break;
          case "hover":
            baseStyle.color = tokens.ACTION_DESTRUCTIVE_DEFAULT;
            break;
          case "active":
            baseStyle.color = tokens.ACTION_DESTRUCTIVE_ACTIVE;
            break;
          case "disabled":
            baseStyle.color = tokens.ACTION_DESTRUCTIVE_DISABLE;
            baseStyle.opacity = 0.6;
            break;
        }
        break;
    }

    return { ...baseStyle, ...style };
  };

  const getTextStyles = (): React.CSSProperties => {
    const buttonTokens =
      size === "large" ? typographyTokens.labelLg : typographyTokens.labelMd;

    const textStyle: React.CSSProperties = {
      fontFamily: buttonTokens.fontFamily,
      fontWeight: parseInt(buttonTokens.fontWeight),
      fontSize: `${buttonTokens.fontSize}px`,
      lineHeight: `${buttonTokens.lineHeight}px`,
      transition: "all 0.2s ease-in-out",
    };

    switch (type) {
      case "primary":
        textStyle.color = tokens.NEUTRAL_80;
        break;
      case "outline":
        switch (buttonState) {
          case "default":
            textStyle.color = tokens.ACTION_TERTIARY_DEFAULT;
            break;
          case "hover":
            textStyle.color = tokens.ACTION_TERTIARY_HOVER;
            break;
          case "active":
            textStyle.color = tokens.ACTION_TERTIARY_ACTIVE;
            break;
          case "disabled":
            textStyle.color = tokens.ACTION_TERTIARY_DISABLE;
            break;
        }
        break;
      case "ghost":
        switch (buttonState) {
          case "default":
            textStyle.color = tokens.ACTION_TERTIARY_DEFAULT;
            break;
          case "hover":
            textStyle.color = tokens.ACTION_TERTIARY_HOVER;
            break;
          case "active":
            textStyle.color = tokens.ACTION_TERTIARY_ACTIVE;
            break;
          case "disabled":
            textStyle.color = tokens.ACTION_TERTIARY_DISABLE;
            break;
        }
        break;
      case "destructive":
        break;
    }

    return textStyle;
  };

  // Icon styles
  const getIconStyles = (): React.CSSProperties => {
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "currentColor",
    };
  };

  // Event handlers for state changes
  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setButtonState("hover");
      onMouseEnter && onMouseEnter(e);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setButtonState("default");
      onMouseLeave && onMouseLeave(e);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setButtonState("active");
      onMouseDown && onMouseDown(e);
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setButtonState("hover");
      onMouseUp && onMouseUp(e);
    }
  };

  return (
    <button
      style={getBaseStyles()}
      onClick={onClick}
      disabled={disabled}
      aria-label={accessibilityLabel || label}
      data-testid={testID}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      {!isIconOnly && leftIcon && (
        <span style={getIconStyles()} className="button-left-icon">
          {leftIconComponent}
        </span>
      )}

      {!isIconOnly && <span style={getTextStyles()}>{label}</span>}

      {isIconOnly && <span style={getIconStyles()}>{iconButton}</span>}

      {!isIconOnly && rightIcon && (
        <span style={getIconStyles()} className="button-right-icon">
          {rightIconComponent}
        </span>
      )}
    </button>
  );
};
